import React, { Component } from "react";
import PropTypes from "prop-types";
import unionBy from "lodash/unionBy";
import differenceBy from "lodash/differenceBy";
import isEqual from "lodash/isEqual";
import Menu from "components/Menu";
import { parameterize } from "util/strings";
import ExternalClickListener from "components/ExternalClickListener";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import MarketLocationSelector from "components/MarketLocationSelector";
import Chip from "./Chip";

import styles from "./styles.module.scss";

export default class LocationFilter extends Component {
  queryStringKeys = ["neighborhoods", "submarkets"];

  constructor(props, context) {
    super(props);
    this.committedValues = context.values;
    this.state = {
      neighborhoods: [...context.values.neighborhoods],
      submarkets: [...context.values.submarkets],
    };
  }

  componentDidUpdate(prevProps) {
    const { open } = this.props;
    const { values } = this.context;
    const committedChanged = this.queryStringKeys.some(
      (key) => !isEqual(values[key], this.committedValues[key])
    );
    this.committedValues = values;
    if (committedChanged || (!prevProps.open && open)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        neighborhoods: [...values.neighborhoods],
        submarkets: [...values.submarkets],
      });
    }
  }

  onNeighborhoodChange = (neighborhood, checked) => {
    const { neighborhoods, submarkets } = this.state;
    if (checked) {
      // checking a neighborhood
      const newNeighborhoods = [...neighborhoods, neighborhood];
      this.setState({ neighborhoods: newNeighborhoods });
    } else {
      const neighborhoodsSubmarket = submarkets.filter(
        ({ neighborhoods: nhoods }) =>
          nhoods.some((nhood) => nhood.id === neighborhood.id)
      );
      if (neighborhoodsSubmarket.length > 0) {
        // unchecking when the submarket is selected
        const newNeighborhoods = neighborhoodsSubmarket[0].neighborhoods
          .filter((n) => n.id !== neighborhood.id)
          .filter(({ visibility }) => visibility === "VISIBLE");
        const newSubmarkets = differenceBy(
          submarkets,
          neighborhoodsSubmarket,
          "externalId"
        );
        this.setState({
          neighborhoods: [...newNeighborhoods, ...neighborhoods],
          submarkets: newSubmarkets,
        });
      } else {
        // unchecking
        const newNeighborhoods = neighborhoods.filter(
          (nhood) =>
            parameterize(nhood.name) !== parameterize(neighborhood.name)
        );

        this.setState({ neighborhoods: newNeighborhoods });
      }
    }
  };

  onSubmarketChange = (submarket, checked) => {
    const arrayHandlerMethod = checked ? unionBy : differenceBy;

    const { neighborhoods, submarkets } = this.state;

    const newNeighborhoods = differenceBy(
      neighborhoods,
      submarket.neighborhoods,
      "externalId"
    );
    const newSubmarkets = arrayHandlerMethod(submarkets, [submarket], "name");
    this.setState({
      neighborhoods: newNeighborhoods,
      submarkets: newSubmarkets,
    });
  };

  onDone = (toggle = true) => {
    const { update } = this.context;
    const { onToggle } = this.props;
    const { neighborhoods, submarkets } = this.state;

    update({
      neighborhoods,
      submarkets,
      page: 1,
    });
    if (toggle) onToggle();
  };

  render() {
    const { values, defaultValues, keysAreDefault } = this.context;
    const { neighborhoods, submarkets } = this.state;
    const { open, onToggle } = this.props;

    return (
      <ExternalClickListener onExternalClick={() => open && this.onDone()}>
        <div className={styles.filterWrapper}>
          <Chip
            onClick={(e) => values.market && onToggle(e)}
            active={open}
            areas={[...values.neighborhoods, ...values.submarkets]}
          />
          <Menu
            data-qa="neighborhoods-filter-menu"
            title="Neighborhoods"
            className={styles.locationFilter}
            visible={open}
            onClose={onToggle}
            onDone={this.onDone}
            onClear={() => {
              this.setState(
                {
                  neighborhoods: defaultValues.neighborhoods,
                  submarkets: defaultValues.submarkets,
                },
                this.onDone
              );
            }}
            isDefault={keysAreDefault(this.queryStringKeys, values)}
          >
            {/*
              We display: none the map below while the menu is closed.
              If we don't do this then parts of the map are still interactive
              even while the menu is closed, preventing interaction with
              certain parts of the site. This only seems to happen on
              mobile Safari.
              It's possibly something to do with the fact the hidden menu uses
              visibility: hidden instead of display: none.
            */}
            {open && values.market && (
              <div className={styles.marketLocationSelector}>
                <MarketLocationSelector
                  accessor="submarkets"
                  showMarketSelector={false}
                  selectedMarket={{ name: values.market.name }}
                  selectedNeighborhoods={neighborhoods}
                  selectedSubmarkets={submarkets}
                  onMarketChange={() => {}}
                  onSubmarketChange={this.onSubmarketChange}
                  onNeighborhoodChange={this.onNeighborhoodChange}
                  selectorsClassName={styles.selectorsClassName}
                />
              </div>
            )}
          </Menu>
        </div>
      </ExternalClickListener>
    );
  }
}

LocationFilter.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

LocationFilter.contextType = QueryManagerContext;
